import React from "react";
import { Card, CardBody, Row, Col, Badge, Button } from "reactstrap";

/**
 * Goddiva India — Fynd return tracking.
 *
 * Driven by the backend `returnTrackingView` which always returns a `stage` for
 * Fynd returns:
 *   - 'failed'    : handoff to Fynd failed → show the error + Sync to retry
 *   - 'initiated' : handed off, waiting for Fynd to create the return shipment
 *   - 'tracking'  : return shipment exists → full lifecycle timeline + courier scans
 *
 * Returns null when `tracking` is null (non-Fynd / UK returns), so it is safe to
 * mount unconditionally. Refund stays a manual admin action; this only surfaces
 * where the parcel is and highlights warehouse arrival.
 */
const FyndReturnTracking = ({ tracking, onSync, syncing }) => {
    if (!tracking) return null;

    const stage = tracking.stage;
    const states = tracking.states || [];
    const currentStatus = tracking.current_status;
    const currentIndex = states.findIndex((s) => s.status === currentStatus);
    const events = tracking.tracking_list || [];

    const SyncButton = () => (
        <Button
            color="primary"
            size="sm"
            outline
            disabled={syncing}
            onClick={() => onSync && onSync()}
        >
            {syncing ? "Syncing…" : "Sync with Fynd"}
        </Button>
    );

    return (
        <Card>
            <CardBody>
                <Row className="mb-3">
                    <Col md={9}>
                        <h6 className="mb-2 font-weight-bold">Return Tracking (Fynd)</h6>
                    </Col>
                    <Col md={3} className="text-right">
                        <SyncButton />
                    </Col>
                </Row>

                {/* FAILED: handoff to Fynd did not go through */}
                {stage === "failed" && (
                    <div className="alert alert-danger" role="alert">
                        <strong>Could not initiate the return on Fynd.</strong>
                        <div className="mt-1">
                            {tracking.error_message || "The return could not be handed off to Fynd. Please try Sync with Fynd, or check the logs."}
                        </div>
                    </div>
                )}

                {/* INITIATED: handed off, awaiting Fynd return shipment */}
                {stage === "initiated" && (
                    <div className="alert alert-info" role="alert">
                        {tracking.message || "Return initiated with Fynd — waiting for Fynd to assign a courier and schedule pickup."}
                    </div>
                )}

                {/* Status / courier summary (shown once we have a return shipment) */}
                {stage === "tracking" && (
                    <Row className="mb-3">
                        <Col md={3}>
                            <strong>Status:</strong>{" "}
                            <Badge color={tracking.reached_warehouse ? "success" : "info"}>
                                {tracking.current_label || "—"}
                            </Badge>
                        </Col>
                        <Col md={3}><strong>Courier:</strong> {tracking.dp_name || "—"}</Col>
                        <Col md={3}><strong>AWB:</strong> {tracking.awb_number || "—"}</Col>
                        <Col md={3}><strong>Last synced:</strong> {tracking.last_synced_at || "—"}</Col>
                    </Row>
                )}

                {stage === "tracking" && tracking.reached_warehouse && !tracking.is_terminal && (
                    <p className="text-success font-weight-bold">
                        Parcel delivered to the warehouse — you may now issue the refund.
                    </p>
                )}

                {/* Canonical Fynd return lifecycle (hidden on failure). */}
                {stage !== "failed" && (
                    <div className="retrun-progress-timeline">
                        {states.map((s, index) => {
                            const isCurrent = index === currentIndex;
                            const isUpcoming = currentIndex >= 0 && index > currentIndex;
                            const marker = isCurrent ? " •" : (currentIndex >= 0 && index < currentIndex ? " ✓" : "");
                            return (
                                <div
                                    key={s.status}
                                    className="retrun-progress-box"
                                    style={{ opacity: isUpcoming ? 0.4 : 1 }}
                                >
                                    <p className="p-0 m-0 text-dark font-weight-bolder">
                                        {s.label}{marker}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Fynd's own courier scan updates, when present. */}
                {stage === "tracking" && events.length > 0 && (
                    <div className="mt-3">
                        <p className="font-weight-bold mb-1">Courier updates</p>
                        <div className="retrun-progress-timeline">
                            {events.map((event, i) => (
                                <div key={i} className="retrun-progress-box">
                                    <p className="p-0 m-0 text-dark font-weight-bolder">
                                        {event.text || event.status}
                                    </p>
                                    {event.time ? <p className="p-0 m-0 text-dark">{event.time}</p> : null}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default FyndReturnTracking;
